import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { forwardRef, useCallback, useRef, useEffect } from "react";

const MapVideoMarkerContent = ({
  position,
  videoSource,
  id,
  onClick,
  refCallback
}: {
  position: google.maps.LatLng;
  videoSource: string;
  id: string;
  onClick: Function,
  refCallback?: (marker: google.maps.marker.AdvancedMarkerElement | null, id: string) => void;
},
  ref: React.Ref<google.maps.marker.AdvancedMarkerElement>) => {
  const internalRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const rewindRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isReversingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const mapContext = useMap();
  const currentZoom = mapContext?.getZoom() || 15;

  const stopReverse = useCallback(() => {
    if (rewindRef.current) {
      clearInterval(rewindRef.current);
      rewindRef.current = null;
    }
  }, []);

  const startReverse = useCallback((video: HTMLVideoElement) => {
    stopReverse();
    isReversingRef.current = true;
    const step = 1 / 30;
    rewindRef.current = setInterval(() => {
      if (!isPlayingRef.current) {
        stopReverse();
        return;
      }
      if (video.currentTime <= 0.05) {
        stopReverse();
        isReversingRef.current = false;
        video.currentTime = 0;
        if (isPlayingRef.current) {
          video.play().catch(() => { });
        }
        return;
      }
      video.currentTime = Math.max(0, video.currentTime - step);
    }, 1000 / 30);
  }, [stopReverse]);

  const pauseVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    isPlayingRef.current = false;
    stopReverse();
    video.pause();
  }, [stopReverse]);

  const playVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    isPlayingRef.current = true;
    if (isReversingRef.current) {
      startReverse(video);
    } else {
      video.play().catch(() => {
        const onCanPlay = () => {
          if (isPlayingRef.current) {
            video.play().catch(() => { });
          }
          video.removeEventListener("canplay", onCanPlay);
        };
        video.addEventListener("canplay", onCanPlay);
      });
    }
  }, [startReverse]);

  const videoCallbackRef = useCallback((video: HTMLVideoElement | null) => {
    if (!video) {
      pauseVideo();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      return;
    }

    videoRef.current = video;

    const handleEnded = () => {
      if (!isPlayingRef.current) return;
      video.pause();
      startReverse(video);
    };

    video.addEventListener("ended", handleEnded);

    // detect when the marker enters/leaves the viewport.
    // This fires when Google Maps shows or hides the marker during zoom/pan.
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            playVideo();
          } else {
            pauseVideo();
          }
        });
      },
      {
        // A small threshold so partially visible markers still play
        threshold: 0.1,
      }
    );
    observerRef.current.observe(video);

    playVideo();

    return () => {
      video.removeEventListener("ended", handleEnded);
      stopReverse();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [playVideo, pauseVideo, startReverse, stopReverse]);

  // pause & clean up on unmount
  useEffect(() => {
    return () => {
      pauseVideo();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [pauseVideo]);

  return (
    <AdvancedMarker
      title={id}
      position={position}
      zIndex={10000}
      onClick={() => onClick(id)}
      ref={(marker) => {
        internalRef.current = marker;
        if (refCallback) refCallback(marker, id);
        if (typeof ref === "function") ref(marker);
        else if (ref)
          (ref as React.MutableRefObject<google.maps.marker.AdvancedMarkerElement | null>).current = marker;
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0px",
          zIndex: 1000,
          transform: `scale(${Math.min(1.0, currentZoom / 18)})`,
        }}
      >
        <div
          style={{
            width: "72px",
            height: "72px",
            borderColor: "black",
            borderWidth: "2px",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            backgroundColor: "#000",
          }}
        >
          <video
            ref={videoCallbackRef}
            width="72"
            height="72"
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          >
            <source src={videoSource} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        {/* Arrow pointer */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: "-28px",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "28px solid #1b2433",
          }}
        />
        <img
          src="/assets/icon_shadow.svg"
          width={24}
          style={{
            display: "block",
            position: "absolute",
            bottom: "-35px",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      </div>
    </AdvancedMarker>
  );
};

export const MapVideoMarker = forwardRef(MapVideoMarkerContent);